"use client";

import Link from "next/link";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { setAffiliation } from "@/features/affiliate/affiliateSlice";
import { OAUTH_APPLE_ENDPOINT, OAUTH_GOOGLE_ENDPOINT } from "@/lib/constant";

import AuthProviderButton from "@/components/AuthProviderButton";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

import appleIcon from "/public/assets/login/login-form/apple.png";
import emailIcon from "/public/assets/login/login-form/email.png";
import googleIcon from "/public/assets/login/login-form/google.png";

type Props = {
	searchParams: { ref?: string };
};

export default function Page({ searchParams: { ref } }: Props) {
	const dispatch = useDispatch();

	useEffect(() => {
		if (ref) dispatch(setAffiliation(ref));
	}, [dispatch, ref]);

	const handleGoogleAuth = () => {
		const searchParams = new URLSearchParams({
			client_id: `${process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}`,
			redirect_uri: `${process.env.NEXT_PUBLIC_FRONTEND_URI}/signup/google`,
			response_type: "code",
			scope: "email profile",
			include_granted_scopes: "true",
		});

		window.location.href = `${OAUTH_GOOGLE_ENDPOINT}?${searchParams}`;
	};

	const handleAppleAuth = () => {
		const searchParams = new URLSearchParams({
			client_id: process.env.NEXT_PUBLIC_APPLE_CLIENT_ID!,
			redirect_uri: `${process.env.NEXT_PUBLIC_FRONTEND_URI}/signup/apple`,
			response_type: "code",
			scope: "name email",
			response_mode: "form_post",
		});
		window.location.href = `${OAUTH_APPLE_ENDPOINT}?${searchParams}`;
	};

	return (
		<Card className="w-full max-w-md rounded-3xl border-none px-1 !shadow-custom sm:px-6">
			<CardHeader className="px-8">
				<CardTitle className="pt-4 text-2xl font-semibold xs:text-3xl">
					Explore
					<span className="text-accent-foreground"> new horizons</span> <br />
					with us
				</CardTitle>
			</CardHeader>
			<CardContent className="px-8 py-6">
				<div className="flex flex-col gap-y-3 py-4 pt-0">
					<AuthProviderButton
						title="Email"
						logo={emailIcon}
						href="/signup/email"
						className="-ml-1"
						iconClassName="w-7"
					/>
					<AuthProviderButton
						title="Google"
						logo={googleIcon}
						onClick={handleGoogleAuth}
					/>
					<AuthProviderButton title="Apple" logo={appleIcon} onClick={handleAppleAuth} />
				</div>
			</CardContent>

			<CardFooter className="flex justify-center gap-1 py-8 text-center text-xs text-muted-foreground">
				<span>Already have an account?</span>
				<Link className="text-accent-foreground underline" href="/login">
					Log in
				</Link>
			</CardFooter>
		</Card>
	);
}
